// src/components/IconPicker.tsx
// Icon selector with categorized SVG icons.
//
// Layout: a fixed-width popover split into a vertical category rail and a
// roomy icon grid. The rail replaces the old horizontally-scrolled category
// strip, which clipped labels mid-word ("Sy…") with no affordance that more
// existed. A vertical list shows every category name in full, never scrolls at
// this count, and keeps the popover's height stable while you type — a wrapped
// chip row would reflow the grid on every filter change.
//
// The picker can also carry the badge colour (`colors` / `color` /
// `onColorChange`): icon and colour are one decision about one badge, so they
// live in one control. Call sites that only need an icon pass nothing extra and
// get the icon-only picker unchanged.

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@core/ui';
import { useI18n } from '@react/hooks';

// ---------------------------------------------------------------------------
// Icon definitions — SVG paths for each icon
// ---------------------------------------------------------------------------

export interface IconDef {
  id: string;
  category: string;
  paths: string[];
  // optional second group of paths (for multi-path icons)
  extras?: { tag: string; attrs: Record<string, string> }[];
}

// Helper to define stroke-based icons (feather-style)
function icon(id: string, category: string, paths: string[]): IconDef {
  return { id, category, paths };
}

const ICON_DEFS: IconDef[] = [
  // Work
  icon('folder', 'work', ['M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z']),
  icon('briefcase', 'work', ['M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z', 'M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16']),
  icon('clipboard', 'work', ['M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2', 'M15 2H9a1 1 0 00-1 1v2a1 1 0 001 1h6a1 1 0 001-1V3a1 1 0 00-1-1z']),
  icon('archive', 'work', ['M21 8v13H3V8', 'M1 3h22v5H1z', 'M10 12h4']),
  icon('inbox', 'work', ['M22 12h-6l-2 3H10l-2-3H2', 'M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z']),
  icon('calendar', 'work', ['M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18']),
  icon('clock', 'work', ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 6v6l4 2']),
  icon('flag', 'work', ['M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z', 'M4 22v-7']),

  // Dev
  icon('code', 'dev', ['M16 18l6-6-6-6', 'M8 6l-6 6 6 6']),
  icon('terminal', 'dev', ['M4 17l6-6-6-6', 'M12 19h8']),
  icon('git-branch', 'dev', ['M6 3v12', 'M18 9a3 3 0 100-6 3 3 0 000 6z', 'M6 21a3 3 0 100-6 3 3 0 000 6z', 'M18 9a9 9 0 01-9 9']),
  icon('git-merge', 'dev', ['M18 21a3 3 0 100-6 3 3 0 000 6z', 'M6 9a3 3 0 100-6 3 3 0 000 6z', 'M6 9v12', 'M6 9a9 9 0 009 9']),
  icon('database', 'dev', ['M12 2C6.477 2 2 3.79 2 6s4.477 4 10 4 10-1.79 10-4-4.477-4-10-4z', 'M2 6v6c0 2.21 4.477 4 10 4s10-1.79 10-4V6', 'M2 12v6c0 2.21 4.477 4 10 4s10-1.79 10-4v-6']),
  icon('server', 'dev', ['M2 4h20v6H2z', 'M2 14h20v6H2z', 'M6 7h.01', 'M6 17h.01']),
  icon('cpu', 'dev', ['M18 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2z', 'M9 9h6v6H9z', 'M9 1v3', 'M15 1v3', 'M9 20v3', 'M15 20v3', 'M20 9h3', 'M20 14h3', 'M1 9h3', 'M1 14h3']),
  icon('globe', 'dev', ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M2 12h20', 'M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z']),

  // Objects
  icon('rocket', 'objects', ['M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z', 'M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z', 'M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0', 'M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5']),
  icon('zap', 'objects', ['M13 2L3 14h9l-1 8 10-12h-9l1-8z']),
  icon('target', 'objects', ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M12 18a6 6 0 100-12 6 6 0 000 12z', 'M12 14a2 2 0 100-4 2 2 0 000 4z']),
  icon('trophy', 'objects', ['M6 9H4.5a2.5 2.5 0 010-5H6', 'M18 9h1.5a2.5 2.5 0 000-5H18', 'M4 22h16', 'M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22', 'M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22', 'M18 2H6v7a6 6 0 1012 0V2z']),
  icon('award', 'objects', ['M12 15a7 7 0 100-14 7 7 0 000 14z', 'M8.21 13.89L7 23l5-3 5 3-1.21-9.12']),
  icon('star', 'objects', ['M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z']),
  icon('heart', 'objects', ['M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z']),
  icon('diamond', 'objects', ['M2.7 10.3a2.41 2.41 0 000 3.41l7.59 7.59a2.41 2.41 0 003.41 0l7.59-7.59a2.41 2.41 0 000-3.41L13.7 2.71a2.41 2.41 0 00-3.41 0L2.7 10.3z']),

  // Charts
  icon('bar-chart', 'charts', ['M12 20V10', 'M18 20V4', 'M6 20v-4']),
  icon('trending-up', 'charts', ['M23 6l-9.5 9.5-5-5L1 18']),
  icon('pie-chart', 'charts', ['M21.21 15.89A10 10 0 118 2.83', 'M22 12A10 10 0 0012 2v10z']),
  icon('activity', 'charts', ['M22 12h-4l-3 9L9 3l-3 9H2']),

  // Nature
  icon('sun', 'nature', ['M12 17a5 5 0 100-10 5 5 0 000 10z', 'M12 1v2', 'M12 21v2', 'M4.22 4.22l1.42 1.42', 'M18.36 18.36l1.42 1.42', 'M1 12h2', 'M21 12h2', 'M4.22 19.78l1.42-1.42', 'M18.36 5.64l1.42-1.42']),
  icon('moon', 'nature', ['M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z']),
  icon('cloud', 'nature', ['M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z']),
  icon('droplet', 'nature', ['M12 2.69l5.66 5.66a8 8 0 11-11.31 0z']),
  icon('leaf', 'nature', ['M11 20A7 7 0 019.8 6.9C15.5 4.9 20 1 20 1s3.04 9.5 3.04 16A7.04 7.04 0 0116 24c-2 0-4-1-5-4', 'M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12']),

  // Shapes & Symbols
  icon('shield', 'symbols', ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z']),
  icon('lock', 'symbols', ['M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z', 'M7 11V7a5 5 0 0110 0v4']),
  icon('key', 'symbols', ['M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4']),
  icon('eye', 'symbols', ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 15a3 3 0 100-6 3 3 0 000 6z']),
  icon('link', 'symbols', ['M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71', 'M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71']),
  icon('hash', 'symbols', ['M4 9h16', 'M4 15h16', 'M10 3L8 21', 'M16 3l-2 18']),
  icon('at-sign', 'symbols', ['M12 16a4 4 0 100-8 4 4 0 000 8z', 'M16 12v1.5a2.5 2.5 0 005 0V12a9 9 0 10-5.5 8.28']),
  icon('compass', 'symbols', ['M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z', 'M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z']),
];

const ALL_CATEGORY = 'all';

const CATEGORIES = [
  { id: ALL_CATEGORY, labelKey: 'iconPicker.categories.all' },
  { id: 'work', labelKey: 'iconPicker.categories.work' },
  { id: 'dev', labelKey: 'iconPicker.categories.dev' },
  { id: 'objects', labelKey: 'iconPicker.categories.objects' },
  { id: 'charts', labelKey: 'iconPicker.categories.charts' },
  { id: 'nature', labelKey: 'iconPicker.categories.nature' },
  { id: 'symbols', labelKey: 'iconPicker.categories.symbols' },
];

/** Icon ids are identifiers ("git-branch"); show them as words in tooltips. */
function humanizeIconId(id: string): string {
  return id.replace(/-/g, ' ');
}

// Columns in the icon grid — also the step size for ArrowUp/ArrowDown.
const GRID_COLS = 6;

// Popover geometry. Wide enough for a rail plus six comfortable columns,
// instead of inheriting the trigger's width.
const POPOVER_WIDTH = 396;
const POPOVER_MAX_HEIGHT = 440;

// ---------------------------------------------------------------------------
// Render an icon by ID
// ---------------------------------------------------------------------------

export function ProjectIcon({ iconId, className, style }: { iconId?: string; className?: string; style?: React.CSSProperties }) {
  const def = ICON_DEFS.find(d => d.id === iconId);
  if (!def) {
    return (
      <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
      </svg>
    );
  }
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      {def.paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Picker component
// ---------------------------------------------------------------------------

export interface IconColorOption {
  /** Raw colour carried by the record — user data, rendered as a swatch. */
  value: string;
  label: string;
}

interface IconPickerProps {
  value: string;
  onChange: (iconId: string) => void;
  className?: string;
  disabled?: boolean;
  /** Accessible name for the trigger; defaults to the picker's own label. */
  'aria-label'?: string;
  /**
   * Colour palette for the badge. Supply it (with `color`/`onColorChange`) to
   * let one control own both halves of the badge. Omit for an icon-only picker.
   */
  colors?: readonly IconColorOption[];
  color?: string;
  onColorChange?: (color: string) => void;
}

export function IconPicker({
  value,
  onChange,
  className,
  disabled,
  'aria-label': ariaLabel,
  colors,
  color,
  onColorChange,
}: IconPickerProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const cellRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const withColor = !!colors && colors.length > 0 && !!onColorChange;

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < POPOVER_MAX_HEIGHT && rect.top > spaceBelow;
    const width = Math.min(POPOVER_WIDTH, window.innerWidth - 16);
    // Keep the panel on screen: it is wider than its trigger, so a trigger near
    // the right edge would otherwise push it off.
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));

    setDropdownStyle({
      position: 'fixed',
      left,
      width,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(POPOVER_MAX_HEIGHT, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setSearch('');
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        close(false);
      }
    };
    const handleScroll = () => updatePosition();
    // Escape lives on the document (the pattern FormDialog uses) so every part
    // of the popover — search, rail, grid, swatches — dismisses the same way.
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close(true);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', handleKey, true);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', handleKey, true);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open, updatePosition, close]);

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const displayIcons = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) {
      return ICON_DEFS.filter((d) => {
        const label = t(`iconPicker.categories.${d.category}`).toLowerCase();
        return humanizeIconId(d.id).includes(q) || d.id.includes(q) || d.category.includes(q) || label.includes(q);
      });
    }
    if (activeCategory === ALL_CATEGORY) return ICON_DEFS;
    return ICON_DEFS.filter((d) => d.category === activeCategory);
  }, [search, activeCategory, t]);

  // Keep the roving index inside the current result set, and park it on the
  // selected icon so opening the picker lands on what is already chosen.
  useEffect(() => {
    const selected = displayIcons.findIndex((d) => d.id === value);
    setFocusedIndex(selected >= 0 ? selected : 0);
  }, [displayIcons, value]);

  const focusCell = (index: number) => {
    setFocusedIndex(index);
    cellRefs.current[index]?.focus();
  };

  const handleGridKeyDown = (e: React.KeyboardEvent) => {
    const last = displayIcons.length - 1;
    if (last < 0) return;
    let next: number | null = null;
    switch (e.key) {
      case 'ArrowRight': next = Math.min(focusedIndex + 1, last); break;
      case 'ArrowLeft': next = Math.max(focusedIndex - 1, 0); break;
      case 'ArrowDown': next = Math.min(focusedIndex + GRID_COLS, last); break;
      case 'ArrowUp':
        next = focusedIndex - GRID_COLS;
        if (next < 0) {
          e.preventDefault();
          inputRef.current?.focus();
          return;
        }
        break;
      case 'Home': next = 0; break;
      case 'End': next = last; break;
      default: return;
    }
    e.preventDefault();
    focusCell(next);
  };

  const handleRailKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const buttons = railRef.current?.querySelectorAll<HTMLButtonElement>('button');
    if (!buttons) return;
    const next = e.key === 'ArrowDown'
      ? Math.min(index + 1, buttons.length - 1)
      : Math.max(index - 1, 0);
    buttons[next]?.focus();
  };

  const handleSelect = (iconId: string) => {
    onChange(iconId);
    close(true);
  };

  const handleClear = () => {
    onChange('');
    close(true);
  };

  const selectCategory = (categoryId: string) => {
    // Search overrides the category filter, so picking one clears the query
    // rather than leaving the grid showing something the rail contradicts.
    setSearch('');
    setActiveCategory(categoryId);
  };

  const triggerLabel = ariaLabel || t('iconPicker.label');

  return (
    <div ref={ref} className={cn('relative inline-block', className)}>
      {/* Trigger — a preview of the badge this control produces */}
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={(e) => {
          // Enter/Space already activate the button; only ArrowDown needs wiring.
          if (!open && e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={triggerLabel}
        className={cn(
          'flex items-center gap-2 rounded-md border border-input bg-background py-1.5 pl-1.5 pr-2 text-sm transition-colors',
          'hover:bg-accent/50 disabled:cursor-not-allowed disabled:opacity-50',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          open && 'ring-2 ring-ring',
        )}
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
          <ProjectIcon
            iconId={value}
            className="h-4 w-4"
            style={withColor && color ? { color } : undefined}
          />
        </span>
        <svg className="h-3.5 w-3.5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Popover via portal */}
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          role="dialog"
          aria-label={triggerLabel}
          className="z-50 flex flex-col overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
          onBlur={(e) => {
            const next = e.relatedTarget as Node | null;
            if (next && (dropdownRef.current?.contains(next) || buttonRef.current?.contains(next))) return;
            if (next) close(false);
          }}
        >
          {/* Search — the fastest path, so it sits first and full width */}
          <div className="flex items-center gap-1.5 border-b border-border p-2">
            <div className="relative flex-1">
              <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' && displayIcons.length > 0) {
                    e.preventDefault();
                    focusCell(focusedIndex);
                  }
                }}
                placeholder={t('iconPicker.searchPlaceholder')}
                aria-label={t('iconPicker.searchLabel')}
                autoCapitalize="off"
                autoComplete="off"
                className="w-full rounded-md border border-input bg-background py-1.5 pl-9 pr-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {!!value && (
              <button
                type="button"
                onClick={handleClear}
                aria-label={t('iconPicker.remove')}
                title={t('iconPicker.remove')}
                className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex min-h-0 flex-1">
            {/* Category rail — every label readable, nothing clipped */}
            <div
              ref={railRef}
              role="group"
              aria-label={t('iconPicker.categoriesLabel')}
              className="flex w-28 shrink-0 flex-col gap-0.5 border-r border-border p-1.5"
            >
              {CATEGORIES.map((cat, i) => {
                const isActive = !search.trim() && activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => selectCategory(cat.id)}
                    onKeyDown={(e) => handleRailKeyDown(e, i)}
                    className={cn(
                      'rounded-md px-2 py-1.5 text-left text-xs font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                    )}
                  >
                    {t(cat.labelKey)}
                  </button>
                );
              })}
            </div>

            {/* Icon grid */}
            <div className="min-w-0 flex-1 overflow-y-auto p-2">
              {displayIcons.length === 0 ? (
                <p className="px-2 py-6 text-center text-sm text-muted-foreground">{t('iconPicker.empty')}</p>
              ) : (
                <div className="grid grid-cols-6 gap-1">
                  {displayIcons.map((iconDef, i) => {
                    const isSelected = value === iconDef.id;
                    return (
                      <button
                        key={iconDef.id}
                        ref={(el) => { cellRefs.current[i] = el; }}
                        type="button"
                        tabIndex={i === focusedIndex ? 0 : -1}
                        aria-pressed={isSelected}
                        aria-label={humanizeIconId(iconDef.id)}
                        title={humanizeIconId(iconDef.id)}
                        onFocus={() => setFocusedIndex(i)}
                        onKeyDown={handleGridKeyDown}
                        onClick={() => handleSelect(iconDef.id)}
                        className={cn(
                          'flex h-10 w-10 items-center justify-center rounded-md transition-colors',
                          'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          isSelected ? 'bg-accent text-accent-foreground ring-1 ring-primary' : 'text-foreground',
                        )}
                      >
                        <ProjectIcon
                          iconId={iconDef.id}
                          className="h-5 w-5"
                          style={withColor && color && isSelected ? { color } : undefined}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Colour — the other half of the same badge */}
          {withColor && (
            <div className="border-t border-border p-2">
              <span className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('iconPicker.colorLabel')}</span>
              <div role="group" aria-label={t('iconPicker.colorLabel')} className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onColorChange?.('')}
                  aria-pressed={!color}
                  aria-label={t('iconPicker.colorNone')}
                  title={t('iconPicker.colorNone')}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-muted-foreground/50 text-muted-foreground transition-all',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover',
                    !color && 'ring-2 ring-foreground ring-offset-2 ring-offset-popover',
                  )}
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" d="M5 19L19 5" />
                  </svg>
                </button>
                {colors?.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onColorChange?.(opt.value)}
                    aria-pressed={color === opt.value}
                    aria-label={opt.label}
                    title={opt.label}
                    className={cn(
                      'h-6 w-6 rounded-full border border-border/60 transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover',
                      color === opt.value && 'ring-2 ring-foreground ring-offset-2 ring-offset-popover',
                    )}
                    style={{ backgroundColor: opt.value }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
